/**
 * Point-of-care care delivery contracts (PR-1 foundation).
 *
 * Binding product/legal semantics for the build scope:
 * docs/operations/ai/handoffs/care-delivery-point-of-care-build-scope-2026-07-22.md
 * especially §20 contract addendum.
 *
 * Dual goal model (non-negotiable):
 * - Plan-authorised goals for a service inclusion are projected onto
 *   services_delivered.goal_ids by Phase 3 execution guard only.
 * - Worker "worked toward" attribution must never be the sole writer of
 *   services_delivered.goal_ids; store on case-note goal links or
 *   visit_goal_attributions instead.
 */

// ---------------------------------------------------------------------------
// Progress note policy (tenant / provider workflow)
// ---------------------------------------------------------------------------

export const PROGRESS_NOTE_POLICIES = [
  'optional',
  'required_every_visit',
  'required_clinical_only',
  'required_first_visit_only',
] as const;

export type ProgressNotePolicy = (typeof PROGRESS_NOTE_POLICIES)[number];

const PROGRESS_NOTE_POLICY_SET: ReadonlySet<string> = new Set(PROGRESS_NOTE_POLICIES);

export function isProgressNotePolicy(
  value: string | null | undefined,
): value is ProgressNotePolicy {
  return typeof value === 'string' && PROGRESS_NOTE_POLICY_SET.has(value);
}

export function parseProgressNotePolicy(
  value: string | null | undefined,
  fallback: ProgressNotePolicy = 'optional',
): ProgressNotePolicy {
  return isProgressNotePolicy(value) ? value : fallback;
}

// ---------------------------------------------------------------------------
// Client confirmation policy + methods + period-close disposition
// ---------------------------------------------------------------------------

export const CLIENT_CONFIRMATION_POLICIES = ['prompted', 'required', 'optional_silent'] as const;

export type ClientConfirmationPolicy = (typeof CLIENT_CONFIRMATION_POLICIES)[number];

const CLIENT_CONFIRMATION_POLICY_SET: ReadonlySet<string> = new Set(CLIENT_CONFIRMATION_POLICIES);

export function isClientConfirmationPolicy(
  value: string | null | undefined,
): value is ClientConfirmationPolicy {
  return typeof value === 'string' && CLIENT_CONFIRMATION_POLICY_SET.has(value);
}

export function parseClientConfirmationPolicy(
  value: string | null | undefined,
  fallback: ClientConfirmationPolicy = 'prompted',
): ClientConfirmationPolicy {
  return isClientConfirmationPolicy(value) ? value : fallback;
}

/** Mobile / RPC allowlist for client_confirmed_method. */
export const CLIENT_CONFIRMATION_METHODS = [
  'verbal',
  'signature',
  'verbal_witnessed',
  'representative',
  'not_obtained',
  /** Coordinator period-close / confirm_sah_service_delivery path */
  'coordinator_attested',
  'portal',
  'family_portal',
  'written',
  'not_required',
] as const;

export type ClientConfirmationMethod = (typeof CLIENT_CONFIRMATION_METHODS)[number];

const CLIENT_CONFIRMATION_METHOD_SET: ReadonlySet<string> = new Set(CLIENT_CONFIRMATION_METHODS);

export function isClientConfirmationMethod(
  value: string | null | undefined,
): value is ClientConfirmationMethod {
  return typeof value === 'string' && CLIENT_CONFIRMATION_METHOD_SET.has(value);
}

/**
 * Methods that set services_delivered.client_confirmed = true (period-close ready).
 * not_obtained leaves client_confirmed=false until coordinator attestation.
 */
export const PARTICIPANT_CONFIRMATION_METHODS: ReadonlySet<ClientConfirmationMethod> = new Set([
  'verbal',
  'signature',
  'verbal_witnessed',
  'representative',
]);

export type ConfirmationDispositionResult = {
  /** Value to write to services_delivered.client_confirmed */
  clientConfirmed: boolean;
  /** Period-close hard gate ready without coordinator intervention */
  periodCloseReady: boolean;
  /** Requires coordinator confirm_sah_service_delivery or disposition path */
  requiresCoordinatorDisposition: boolean;
  method: ClientConfirmationMethod | null;
};

/**
 * §20.1 confirmation disposition model.
 * Mobile never sets client_confirmed=true for not_obtained.
 */
export function resolveConfirmationDisposition(input: {
  method: string | null | undefined;
  /** Coordinator attestation after review of composite pack */
  coordinatorAttested?: boolean;
}): ConfirmationDispositionResult {
  if (input.coordinatorAttested) {
    return {
      clientConfirmed: true,
      periodCloseReady: true,
      requiresCoordinatorDisposition: false,
      method: isClientConfirmationMethod(input.method) ? input.method : null,
    };
  }

  if (!isClientConfirmationMethod(input.method)) {
    return {
      clientConfirmed: false,
      periodCloseReady: false,
      requiresCoordinatorDisposition: true,
      method: null,
    };
  }

  if (input.method === 'not_obtained') {
    return {
      clientConfirmed: false,
      periodCloseReady: false,
      requiresCoordinatorDisposition: true,
      method: 'not_obtained',
    };
  }

  if (PARTICIPANT_CONFIRMATION_METHODS.has(input.method)) {
    return {
      clientConfirmed: true,
      periodCloseReady: true,
      requiresCoordinatorDisposition: false,
      method: input.method,
    };
  }

  return {
    clientConfirmed: false,
    periodCloseReady: false,
    requiresCoordinatorDisposition: true,
    method: input.method,
  };
}

export function confirmationBlocksMobileComplete(
  policy: ClientConfirmationPolicy,
  method: string | null | undefined,
): boolean {
  if (policy !== 'required') return false;
  if (!isClientConfirmationMethod(method)) return true;
  return method === 'not_obtained';
}

// ---------------------------------------------------------------------------
// Not-obtained reason codes
// ---------------------------------------------------------------------------

export const CONFIRMATION_NOT_OBTAINED_REASONS = [
  'capacity',
  'refusal',
  'alone_no_witness',
  'asleep_resting',
  'language_barrier',
  'emergency_priority',
  'other',
] as const;

export type ConfirmationNotObtainedReason = (typeof CONFIRMATION_NOT_OBTAINED_REASONS)[number];

const NOT_OBTAINED_REASON_SET: ReadonlySet<string> = new Set(CONFIRMATION_NOT_OBTAINED_REASONS);

export function isConfirmationNotObtainedReason(
  value: string | null | undefined,
): value is ConfirmationNotObtainedReason {
  return typeof value === 'string' && NOT_OBTAINED_REASON_SET.has(value);
}

// ---------------------------------------------------------------------------
// Structured tasks_completed (schemaVersion 1)
// ---------------------------------------------------------------------------

export const TASKS_COMPLETED_SCHEMA_VERSION = 1 as const;

export const DELIVERY_TASK_KINDS = ['worker_instruction', 'intervention'] as const;
export type DeliveryTaskKind = (typeof DELIVERY_TASK_KINDS)[number];

export const DELIVERY_TASK_STATUSES = ['done', 'not_applicable', 'deferred'] as const;
export type DeliveryTaskStatus = (typeof DELIVERY_TASK_STATUSES)[number];

export type DeliveryTaskCompletedItem = {
  instructionId: string;
  kind: DeliveryTaskKind;
  status: DeliveryTaskStatus;
  completedAt: string;
};

export type TasksCompletedPayload = {
  schemaVersion: typeof TASKS_COMPLETED_SCHEMA_VERSION;
  items: DeliveryTaskCompletedItem[];
};

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function isUuid(value: string): boolean {
  return UUID_RE.test(value);
}

export type TasksCompletedValidation =
  | { ok: true; value: TasksCompletedPayload }
  | { ok: false; error: string };

/**
 * Validates structured tasks_completed. Rejects garbage {} / [true] shapes
 * that would otherwise satisfy Phase 3 non-empty check.
 */
export function parseTasksCompletedPayload(raw: unknown): TasksCompletedValidation {
  if (raw == null) {
    return { ok: false, error: 'tasks_completed_missing' };
  }
  if (typeof raw !== 'object' || Array.isArray(raw)) {
    return { ok: false, error: 'tasks_completed_invalid_shape' };
  }
  const obj = raw as Record<string, unknown>;
  const version = obj.schemaVersion ?? obj.schema_version;
  if (version !== TASKS_COMPLETED_SCHEMA_VERSION && version !== 1) {
    return { ok: false, error: 'tasks_completed_schema_version' };
  }
  const itemsRaw = obj.items;
  if (!Array.isArray(itemsRaw)) {
    return { ok: false, error: 'tasks_completed_items_required' };
  }
  if (itemsRaw.length === 0) {
    return { ok: false, error: 'tasks_completed_items_empty' };
  }
  if (itemsRaw.length > 100) {
    return { ok: false, error: 'tasks_completed_items_limit' };
  }

  const items: DeliveryTaskCompletedItem[] = [];
  for (let i = 0; i < itemsRaw.length; i++) {
    const row = itemsRaw[i];
    if (!row || typeof row !== 'object' || Array.isArray(row)) {
      return { ok: false, error: `tasks_completed_item_invalid:${i}` };
    }
    const r = row as Record<string, unknown>;
    const instructionId = String(r.instructionId ?? r.instruction_id ?? '');
    if (!isUuid(instructionId)) {
      return { ok: false, error: `tasks_completed_instruction_id:${i}` };
    }
    const kind = String(r.kind ?? '');
    if (kind !== 'worker_instruction' && kind !== 'intervention') {
      return { ok: false, error: `tasks_completed_kind:${i}` };
    }
    const status = String(r.status ?? '');
    if (status !== 'done' && status !== 'not_applicable' && status !== 'deferred') {
      return { ok: false, error: `tasks_completed_status:${i}` };
    }
    const completedAt = String(r.completedAt ?? r.completed_at ?? '');
    if (!completedAt || Number.isNaN(Date.parse(completedAt))) {
      return { ok: false, error: `tasks_completed_completed_at:${i}` };
    }
    items.push({
      instructionId,
      kind,
      status,
      completedAt,
    });
  }

  return {
    ok: true,
    value: { schemaVersion: TASKS_COMPLETED_SCHEMA_VERSION, items },
  };
}

/** Phase 3 non-empty evidence helper — structured payload only (not legacy junk). */
export function tasksCompletedSatisfiesPhase3Evidence(raw: unknown): boolean {
  return parseTasksCompletedPayload(raw).ok;
}

// ---------------------------------------------------------------------------
// First-visit detection (client-side advisory; server is authoritative)
// ---------------------------------------------------------------------------

export type FirstVisitKey = {
  tenantId: string;
  clientId: string;
  carePlanServiceInclusionId: string;
  /** Plan version of the inclusion's care plan — resets first-visit on version change (OQ2/OQ8). */
  carePlanVersion: number | null;
};

export function firstVisitCacheKey(key: FirstVisitKey): string {
  const version = key.carePlanVersion == null ? 'vnull' : `v${key.carePlanVersion}`;
  return `first-visit:${key.tenantId}:${key.clientId}:${key.carePlanServiceInclusionId}:${version}`;
}

/**
 * Advisory first-visit flag for UX.
 * - knownCompletedCount === 0 → first
 * - knownCompletedCount > 0 → subsequent
 * - unknown (null) → treat as first offline (fail-strict §20.4)
 */
export function resolveIsFirstVisit(knownCompletedCount: number | null | undefined): {
  isFirstVisit: boolean;
  source: 'known_zero' | 'known_subsequent' | 'unknown_strict';
} {
  if (knownCompletedCount == null || Number.isNaN(knownCompletedCount)) {
    return { isFirstVisit: true, source: 'unknown_strict' };
  }
  if (knownCompletedCount <= 0) {
    return { isFirstVisit: true, source: 'known_zero' };
  }
  return { isFirstVisit: false, source: 'known_subsequent' };
}

/**
 * Server-side first-visit winner selection after serialisation.
 * Deterministic: earliest actual_end, then lowest id.
 */
export function selectFirstVisitWinner<
  T extends { id: string; actualEnd: string | null; completedAt?: string | null },
>(rows: readonly T[]): T | null {
  if (rows.length === 0) return null;
  const sorted = [...rows].sort((a, b) => {
    const aTime = a.actualEnd || a.completedAt || '';
    const bTime = b.actualEnd || b.completedAt || '';
    if (aTime !== bTime) return aTime < bTime ? -1 : 1;
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });
  return sorted[0] ?? null;
}

// ---------------------------------------------------------------------------
// Checklist mandatory categories (SAH v1)
// ---------------------------------------------------------------------------

/**
 * Service categories where first-visit checklist is mandatory when worker
 * instructions or interventions exist. Aligns with mobile HOME_SAH categories
 * (lowercased underscore form).
 */
export const FIRST_VISIT_CHECKLIST_CATEGORIES: ReadonlySet<string> = new Set([
  'clinical',
  'clinical_care',
  'nursing',
  'allied_health',
  'independence',
  'personal_care',
  'social_support',
  'everyday_living',
  'domestic',
  'domestic_assistance',
  'cleaning',
  'home_maintenance',
]);

/** Categories excluded unless worker instructions are explicitly present. */
export const FIRST_VISIT_CHECKLIST_OPT_IN_ONLY: ReadonlySet<string> = new Set([
  'meals',
  'transport',
  'assistive_technology',
  'home_modification',
  'nutrition',
]);

export function normaliseServiceCategory(value: string | null | undefined): string {
  if (!value) return '';
  return value
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
}

/**
 * Whether first-visit checklist hard gate applies for this category.
 * Server must re-derive; mobile is advisory.
 */
export function firstVisitChecklistApplies(input: {
  serviceCategory: string | null | undefined;
  hasWorkerInstructions: boolean;
  hasInterventions: boolean;
}): boolean {
  const cat = normaliseServiceCategory(input.serviceCategory);
  if (!cat) return input.hasWorkerInstructions || input.hasInterventions;
  if (FIRST_VISIT_CHECKLIST_OPT_IN_ONLY.has(cat)) {
    return input.hasWorkerInstructions || input.hasInterventions;
  }
  if (FIRST_VISIT_CHECKLIST_CATEGORIES.has(cat)) {
    return input.hasWorkerInstructions || input.hasInterventions;
  }
  // Unknown category: only if plan authored instructions/interventions
  return input.hasWorkerInstructions || input.hasInterventions;
}

export type ChecklistCompleteness = {
  complete: boolean;
  missingRequiredIds: string[];
};

/**
 * First-visit hard gate: every required instruction must be status=done
 * (or not_applicable with policy allow — default not_applicable counts as complete
 * only if allowNotApplicable=true).
 */
export function evaluateFirstVisitChecklist(input: {
  requiredInstructionIds: readonly string[];
  tasksCompleted: TasksCompletedPayload | null;
  allowNotApplicable?: boolean;
}): ChecklistCompleteness {
  const allowNa = input.allowNotApplicable === true;
  if (input.requiredInstructionIds.length === 0) {
    return { complete: true, missingRequiredIds: [] };
  }
  const byId = new Map(
    (input.tasksCompleted?.items ?? []).map((item) => [item.instructionId, item]),
  );
  const missing: string[] = [];
  for (const id of input.requiredInstructionIds) {
    const item = byId.get(id);
    if (!item) {
      missing.push(id);
      continue;
    }
    if (item.status === 'done') continue;
    if (item.status === 'not_applicable' && allowNa) continue;
    missing.push(id);
  }
  return { complete: missing.length === 0, missingRequiredIds: missing };
}

// ---------------------------------------------------------------------------
// Progress note requirement evaluation
// ---------------------------------------------------------------------------

export function progressNoteRequired(input: {
  policy: ProgressNotePolicy;
  isFirstVisit: boolean;
  serviceCategory: string | null | undefined;
}): boolean {
  switch (input.policy) {
    case 'optional':
      return false;
    case 'required_every_visit':
      return true;
    case 'required_first_visit_only':
      return input.isFirstVisit;
    case 'required_clinical_only': {
      const cat = normaliseServiceCategory(input.serviceCategory);
      return (
        cat === 'clinical' ||
        cat === 'clinical_care' ||
        cat === 'nursing' ||
        cat === 'allied_health'
      );
    }
    default:
      return false;
  }
}

// ---------------------------------------------------------------------------
// Dual goal semantics
// ---------------------------------------------------------------------------

export type PlanServiceGoal = {
  goalId: string;
  title?: string | null;
};

export type WorkerGoalAttribution = {
  goalId: string;
  attribution: 'PROGRESS' | 'WORKED_TOWARD' | 'DISCUSSED';
};

/**
 * Strip worker goal_ids from a complete payload that would incorrectly land
 * on services_delivered.goal_ids. Returns payload without goal_ids/goalIds.
 */
export function stripWorkerGoalIdsFromCompletePayload<T extends Record<string, unknown>>(
  payload: T,
): Omit<T, 'goal_ids' | 'goalIds'> {
  const { goal_ids: _g1, goalIds: _g2, ...rest } = payload;
  void _g1;
  void _g2;
  return rest;
}

/**
 * Post-complete regression helper for Phase 3 rows.
 * Delivery row goal_ids must equal plan-projected links (order-insensitive).
 * Worker payload goal_ids are irrelevant once projection has run — if the
 * stored row equals the worker payload *and* differs from plan projection,
 * treat as a contract violation (worker goals owned the row).
 */
export function assertWorkerGoalsNotOnDeliveryRow(input: {
  /** Goals stored on services_delivered.goal_ids after complete */
  storedDeliveryGoalIds: readonly string[];
  /** What mobile attempted to send as services_delivered.goal_ids */
  workerPayloadGoalIds?: readonly string[] | null;
  /** What Phase 3 / plan links project (authoritative) */
  planProjectedGoalIds: readonly string[];
}):
  | { ok: true }
  | {
      ok: false;
      error: 'worker_goal_ids_must_not_own_delivery_row' | 'delivery_goal_ids_mismatch_plan';
    } {
  const sortCopy = (ids: readonly string[]) => [...ids].map(String).sort();
  const stored = sortCopy(input.storedDeliveryGoalIds);
  const plan = sortCopy(input.planProjectedGoalIds);
  if (stored.length !== plan.length || stored.some((id, i) => id !== plan[i])) {
    return { ok: false, error: 'delivery_goal_ids_mismatch_plan' };
  }
  const worker = input.workerPayloadGoalIds;
  if (worker && worker.length > 0) {
    const workerSorted = sortCopy(worker);
    const workerOwnsRow =
      workerSorted.length === stored.length &&
      workerSorted.every((id, i) => id === stored[i]) &&
      (workerSorted.length !== plan.length || workerSorted.some((id, i) => id !== plan[i]));
    // If worker payload equals stored AND stored already matches plan, OK (coincidence).
    // If worker payload would have been the only source and plan differs — caught above.
    void workerOwnsRow;
  }
  return { ok: true };
}

/**
 * True when a complete payload still carries worker goal_ids that must be stripped
 * before RPC (mobile preflight).
 */
export function completePayloadHasWorkerGoalIds(payload: Record<string, unknown>): boolean {
  const a = payload.goal_ids;
  const b = payload.goalIds;
  return (Array.isArray(a) && a.length > 0) || (Array.isArray(b) && b.length > 0);
}

/**
 * not_obtained requires a reason code (and free text when reason=other).
 */
export function validateNotObtainedReason(input: {
  method: string | null | undefined;
  reason: string | null | undefined;
  otherDetail?: string | null;
}): { ok: true } | { ok: false; error: string } {
  if (input.method !== 'not_obtained') return { ok: true };
  if (!isConfirmationNotObtainedReason(input.reason)) {
    return { ok: false, error: 'not_obtained_reason_required' };
  }
  if (input.reason === 'other' && !String(input.otherDetail ?? '').trim()) {
    return { ok: false, error: 'not_obtained_other_detail_required' };
  }
  return { ok: true };
}

// ---------------------------------------------------------------------------
// Delivery brief (v1 plan-level + category filter)
// ---------------------------------------------------------------------------

export type DeliveryBriefInstruction = {
  id: string;
  kind: DeliveryTaskKind;
  headline: string;
  detail: string;
  mustRead: boolean;
  mandatoryOnFirstVisit: boolean;
};

export type DeliveryBrief = {
  /** Contract version for offline pack staleness */
  briefVersion: 2;
  carePlanId: string | null;
  carePlanVersion: number | null;
  carePlanServiceInclusionId: string | null;
  serviceCategory: string | null;
  isFirstVisit: boolean;
  isFirstVisitSource: 'known_zero' | 'known_subsequent' | 'unknown_strict';
  planGoals: PlanServiceGoal[];
  instructions: DeliveryBriefInstruction[];
  progressNotePolicy: ProgressNotePolicy;
  clientConfirmationPolicy: ClientConfirmationPolicy;
};

export function requiredFirstVisitInstructionIds(brief: DeliveryBrief): string[] {
  if (!brief.isFirstVisit) return [];
  if (
    !firstVisitChecklistApplies({
      serviceCategory: brief.serviceCategory,
      hasWorkerInstructions: brief.instructions.some((i) => i.kind === 'worker_instruction'),
      hasInterventions: brief.instructions.some((i) => i.kind === 'intervention'),
    })
  ) {
    return [];
  }
  return brief.instructions.filter((i) => i.mandatoryOnFirstVisit || i.mustRead).map((i) => i.id);
}

/**
 * Normalise service category / code tokens for inclusion-scope matching.
 */
export function normaliseDeliveryServiceToken(value: string | null | undefined): string {
  return (value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
}

/**
 * Whether an intervention type is relevant to the visit's service line.
 * Plan rows are not FK-scoped to inclusions today — match by type/category tokens.
 * Unknown / empty intervention types are kept (fail open for safety).
 */
export function interventionMatchesServiceScope(input: {
  interventionType?: string | null;
  serviceCategory?: string | null;
  serviceCode?: string | null;
}): boolean {
  const type = normaliseDeliveryServiceToken(input.interventionType);
  if (!type) return true;

  const category = normaliseDeliveryServiceToken(input.serviceCategory);
  const code = normaliseDeliveryServiceToken(input.serviceCode);
  const haystack = `${category} ${code}`.trim();
  if (!haystack) return true;

  // Direct / substring match (e.g. personal_care ↔ personal_care)
  if (category && (type === category || type.includes(category) || category.includes(type))) {
    return true;
  }
  if (code && (type === code || type.includes(code) || code.includes(type))) {
    return true;
  }

  // Category family buckets for SAH-style lines
  const clinicalFamily = /clinical|nurs|medicat|wound|palliativ|obs|vital/;
  const independenceFamily = /independ|personal|hygiene|shower|dress|mobil|falls|transfer/;
  const livingFamily = /everyday|domestic|meal|household|shopping|laundry|clean/;
  const socialFamily = /social|compan|community|transport|respite/;

  if (clinicalFamily.test(haystack) && clinicalFamily.test(type)) return true;
  if (independenceFamily.test(haystack) && independenceFamily.test(type)) return true;
  if (livingFamily.test(haystack) && livingFamily.test(type)) return true;
  if (socialFamily.test(haystack) && socialFamily.test(type)) return true;

  return false;
}

/**
 * Build delivery brief instructions.
 *
 * When currentServiceInclusionId is provided (visit inclusion context):
 * - Always include must-read worker instructions (doorstep critical)
 * - Include exact-inclusion interventions as first-visit mandatory
 * - Include plan-wide interventions as visible, non-mandatory context
 * - Exclude interventions linked to another service inclusion
 * - Never fall back from no exact match to every intervention
 *
 * mustRead worker instructions are mandatory on first visit.
 */
export function buildDeliveryBriefInstructionsV1(input: {
  workerInstructions?: ReadonlyArray<{
    id: string;
    headline: string;
    detail: string;
    mustRead?: boolean;
    /** Optional link to an intervention, used to exclude another service's tip. */
    interventionId?: string | null;
  }>;
  interventions?: ReadonlyArray<{
    id: string;
    instruction: string;
    interventionType?: string | null;
    carePlanServiceInclusionId?: string | null;
    isFundedService?: boolean;
  }>;
  /** Exact care_plan_service_inclusions.id for this visit line. */
  currentServiceInclusionId?: string | null;
  /** Retained for older callers and display context; never authorises scope. */
  serviceCategory?: string | null;
  serviceCode?: string | null;
}): DeliveryBriefInstruction[] {
  const out: DeliveryBriefInstruction[] = [];
  const allInterventions = input.interventions ?? [];
  const currentInclusionId = input.currentServiceInclusionId?.trim() || null;
  const includedInterventions = currentInclusionId
    ? allInterventions.filter((intervention) => {
        const linkedInclusionId = intervention.carePlanServiceInclusionId?.trim() || null;
        if (linkedInclusionId) return linkedInclusionId === currentInclusionId;
        return intervention.isFundedService !== true;
      })
    : allInterventions;
  const includedInterventionIds = new Set(
    includedInterventions.map((intervention) => intervention.id),
  );

  for (const w of input.workerInstructions ?? []) {
    const linkedInterventionId = w.interventionId?.trim() || null;
    if (
      w.mustRead !== true &&
      linkedInterventionId &&
      !includedInterventionIds.has(linkedInterventionId)
    ) {
      continue;
    }
    out.push({
      id: w.id,
      kind: 'worker_instruction',
      headline: w.headline,
      detail: w.detail,
      mustRead: w.mustRead === true,
      mandatoryOnFirstVisit: w.mustRead === true,
    });
  }

  for (const iv of includedInterventions) {
    const linkedInclusionId = iv.carePlanServiceInclusionId?.trim() || null;
    const exactInclusionMatch = Boolean(
      currentInclusionId && linkedInclusionId === currentInclusionId,
    );
    out.push({
      id: iv.id,
      kind: 'intervention',
      headline: iv.interventionType?.trim() || 'Intervention',
      detail: iv.instruction,
      mustRead: false,
      // Only an exact FK-bound service intervention can become mandatory for
      // this visit. Plan-wide or legacy-unscoped context stays visible without
      // turning uncertain scope into worker friction.
      mandatoryOnFirstVisit: exactInclusionMatch,
    });
  }
  return out;
}

// ---------------------------------------------------------------------------
// Offline multi-artefact evidence group
// ---------------------------------------------------------------------------

export const DELIVERY_EVIDENCE_OP_ORDER = [
  'VISIT_COMPLETE',
  'CASE_NOTE_SAVE',
  'SERVICE_DELIVERY_SIGNATURE',
] as const;

export type DeliveryEvidenceOpType = (typeof DELIVERY_EVIDENCE_OP_ORDER)[number];

export function deliveryEvidenceOpSortKey(type: string): number {
  const idx = DELIVERY_EVIDENCE_OP_ORDER.indexOf(type as DeliveryEvidenceOpType);
  return idx === -1 ? 99 : idx;
}

export function sortDeliveryEvidenceOps<T extends { type: string }>(ops: readonly T[]): T[] {
  return [...ops].sort(
    (a, b) => deliveryEvidenceOpSortKey(a.type) - deliveryEvidenceOpSortKey(b.type),
  );
}

// ---------------------------------------------------------------------------
// Clock override phases (payroll-safe)
// ---------------------------------------------------------------------------

export const CLOCK_OVERRIDE_PHASES = [
  'A_PRE_TIMESHEET',
  'B_UNLOCKED_TIMESHEET',
  'C_PAY_RUN_LOCKED',
] as const;
export type ClockOverridePhase = (typeof CLOCK_OVERRIDE_PHASES)[number];

export const CLOCK_OVERRIDE_REASONS = [
  'forgot_clock_in',
  'forgot_clock_out',
  'device_failure',
  'no_signal',
  'other',
] as const;

export type ClockOverrideReason = (typeof CLOCK_OVERRIDE_REASONS)[number];

export function resolveClockOverridePhase(input: {
  timesheetExists: boolean;
  timesheetLocked: boolean;
  payRunId: string | null | undefined;
}): ClockOverridePhase {
  if (input.payRunId || input.timesheetLocked) return 'C_PAY_RUN_LOCKED';
  if (input.timesheetExists) return 'B_UNLOCKED_TIMESHEET';
  return 'A_PRE_TIMESHEET';
}

export function clockOverrideAllowedForWorkerSelfService(phase: ClockOverridePhase): boolean {
  return phase === 'A_PRE_TIMESHEET' || phase === 'B_UNLOCKED_TIMESHEET';
}

// ---------------------------------------------------------------------------
// Complete step machine (UX contract)
// ---------------------------------------------------------------------------

export const VISIT_COMPLETE_STEPS = [
  'summary_checklist',
  'progress_note',
  'confirmation',
  'clock_alignment',
  'submit',
] as const;

export type VisitCompleteStep = (typeof VISIT_COMPLETE_STEPS)[number];

export function nextVisitCompleteSteps(input: {
  isFirstVisit: boolean;
  checklistRequired: boolean;
  progressNoteRequired: boolean;
  confirmationPolicy: ClientConfirmationPolicy;
}): VisitCompleteStep[] {
  const steps: VisitCompleteStep[] = ['summary_checklist'];
  if (input.progressNoteRequired) steps.push('progress_note');
  if (input.confirmationPolicy !== 'optional_silent') steps.push('confirmation');
  steps.push('clock_alignment', 'submit');
  // Subsequent visits keep the same step list but checklist is non-blocking —
  // UI shortens copy; step machine stays stable.
  void input.isFirstVisit;
  void input.checklistRequired;
  return steps;
}

// ---------------------------------------------------------------------------
// Composite confirmation pack labels (UI / web evidence)
// ---------------------------------------------------------------------------

export type CompositeConfirmationLayer =
  | 'L1_attendance'
  | 'L2_service_record'
  | 'L3_care_narrative'
  | 'L4_plan_fidelity'
  | 'L5_participant_confirmation'
  | 'L6_exception';

export const COMPOSITE_CONFIRMATION_LAYER_LABELS: Record<CompositeConfirmationLayer, string> = {
  L1_attendance: 'Worker attendance (clock in/out)',
  L2_service_record: 'Service delivery record',
  L3_care_narrative: 'Progress / care notes',
  L4_plan_fidelity: 'Plan instruction checklist',
  L5_participant_confirmation: 'Participant confirmation',
  L6_exception: 'Exception / no-show',
};

/**
 * SAH Manual §10.5 help copy for workers (not legal advice).
 */
export const SAH_CONFIRMATION_HELP_COPY =
  'Clock times and care notes satisfy Support at Home confirmation-of-delivery evidence. Participant confirmation strengthens transparency and dispute handling.';
